/**
 * Импорт топа моделей из CRM (спека 2026-10-27-crm-top-import-design.md): типы платья + «Топ N» → модели по
 * сделкам с новыми фото → очередь импорта по одной модели. Плитки рисуются порциями по 120 при прокрутке.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Ban, Check } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { btn, btnGhost, btnPrimary, ErrorLine, input } from "@/components/trends/ui";
import { GROUPS, setMany, type CrmPhoto } from "@/lib/crmImport";
import { dealsLabel, drawnBlocks, excludedLabel, modelChecked, ruDate, unhide, uncheckModel, visibleModels, parseLimit, reconcileChecked, queueBodies, runQueue, summarize, topInitialChecked, type StepResult } from "@/lib/crmTopImport";
import { num } from "@/lib/media";
import { excludeCrmModel, loadCrmTop, loadCrmTypes, restoreCrmModel, photoKeys, runCrmImport, type CrmTopData, type CrmTopModel } from "@/lib/photos";
import { retryQuery } from "@/lib/trends";
import { Dialog } from "@/shared/ui/Dialog";

const PAGE = 120;
const KIND: Record<CrmPhoto["kind"], string> = { main: "главное", mp: "маркетплейс", front: "вид спереди", other: "прочее" };
const GROUP_TITLE = Object.fromEntries(GROUPS.map((g) => [g.key, g.title])) as Record<CrmPhoto["group"], string>;
const topKey = (types: number[], limit: number) => ["photos", "crm-top", types.join(","), limit] as const;

/** Метка под сеткой: подъехала к экрану окна — просит следующую порцию. */
function More({ onMore, text }: { onMore: () => void; text: string }) {
  const mark = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = mark.current;
    if (!el) return;
    let fired = false;
    const obs = new IntersectionObserver(
      (e) => {
        if (fired || !e.some((x) => x.isIntersecting)) return;
        fired = true;
        onMore();
      },
      { root: el.closest("dialog"), rootMargin: "1200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [onMore]);
  return (
    <div ref={mark} className="pt-2 text-center text-[12px] text-fg-faint tnum">
      {text}
    </div>
  );
}

export function CrmTopImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Пока идёт очередь, окно не закрывается: прогресс и «Остановить» должны оставаться на виду.
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) setBusy(false);
  }, [open]);
  return (
    <Dialog open={open} title="Импорт топа из CRM" onClose={() => (busy ? undefined : onClose())} wide locked={busy}>
      {open ? <Body busy={busy} onBusy={setBusy} /> : null}
    </Dialog>
  );
}

function Body({ busy, onBusy }: { busy: boolean; onBusy: (b: boolean) => void }) {
  const typesQ = useQuery({ queryKey: ["photos", "crm-types"], queryFn: loadCrmTypes, retry: retryQuery });
  const [picked, setPicked] = useState<number[] | null>(null);
  const [limitText, setLimitText] = useState("200");
  const [asked, setAsked] = useState<{ types: number[]; limit: number } | null>(null);
  const types = typesQ.data?.types;
  // Умолчание — «Свадебное», если есть.
  const sel = picked ?? (types ? types.filter((t) => t.name === "Свадебное").map((t) => t.id) : []);
  const limit = parseLimit(limitText);
  return (
    <div className="grid gap-4 p-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (limit !== null && sel.length) setAsked({ types: sel, limit });
        }}
      >
        {typesQ.isPending ? <span className="text-[12.5px] text-fg-faint">Загружаю типы…</span> : null}
        {types?.map((t) => {
          const on = sel.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              aria-pressed={on}
              disabled={busy}
              className={clsx(btn, on && "border-accent text-accent-text")}
              onClick={() => setPicked(on ? sel.filter((x) => x !== t.id) : [...sel, t.id])}
            >
              {t.name}
            </button>
          );
        })}
        <label className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
          Топ
          <input
            aria-label="Топ"
            className={clsx(input, "w-20", limit === null && "border-warn")}
            inputMode="numeric"
            disabled={busy}
            value={limitText}
            onChange={(e) => setLimitText(e.target.value)}
          />
        </label>
        <button type="submit" className={btn} disabled={busy || limit === null || sel.length === 0}>
          Показать
        </button>
        {sel.length === 0 && types ? <span className="text-[12px] text-warn">выберите тип платья</span> : null}
      </form>
      <ErrorLine error={typesQ.error} />
      {limit === null ? <span className="text-[12px] text-warn">Топ — целое число от 1 до 500</span> : null}
      {asked ? <Top key={topKey(asked.types, asked.limit).join("|")} onBusy={onBusy} types={asked.types} limit={asked.limit} /> : null}
    </div>
  );
}

function Top({ types, limit, onBusy }: { types: number[]; limit: number; onBusy: (b: boolean) => void }) {
  const q = useQuery({ queryKey: topKey(types, limit), queryFn: () => loadCrmTop(types, limit), retry: retryQuery, refetchOnWindowFocus: false });
  if (q.isPending) return <div className="text-[12.5px] text-fg-faint">Считаю топ по сделкам…</div>;
  // Неудачное фоновое обновление оставляет data: не размонтируем окно, чтобы не потерять итог и ошибки.
  if (!q.data) return <ErrorLine error={q.error} />;
  return (
    <div className="grid gap-4">
      {q.isError ? <ErrorLine error={q.error} /> : null}
      <TopBody data={q.data} onBusy={onBusy} types={types} limit={limit} />
    </div>
  );
}

function TopBody({ data, types, limit, onBusy }: { data: CrmTopData; types: number[]; limit: number; onBusy: (b: boolean) => void }) {
  const client = useQueryClient();
  const { score_error } = data;
  // Локально скрытые (оптимистично) до ответа сервера: с обновлённым топом они и так исчезают.
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  const models = useMemo(() => visibleModels(data.models, hidden), [data.models, hidden]);
  const [exErr, setExErr] = useState<Record<number, string>>({});
  const [restoring, setRestoring] = useState<number | null>(null);
  const [restoreErr, setRestoreErr] = useState<string | null>(null);
  const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
  const refresh = () => client.invalidateQueries({ queryKey: topKey(types, limit) });
  const exclude = async (m: CrmTopModel) => {
    setExErr(({ [m.model_id]: _, ...rest }) => rest);
    setHidden((h) => new Set(h).add(m.model_id));
    setChecked((c) => uncheckModel(c, m));
    try {
      await excludeCrmModel(m.model_id);
      await refresh();
    } catch (e) {
      setHidden((h) => {
        const n = new Set(h);
        n.delete(m.model_id);
        return n;
      });
      setExErr((x) => ({ ...x, [m.model_id]: errMsg(e) }));
    }
  };
  const restore = async (id: number) => {
    setRestoring(id);
    setRestoreErr(null);
    try {
      await restoreCrmModel(id);
      setHidden((h) => unhide(h, id));
      await refresh();
    } catch (e) {
      setRestoreErr(errMsg(e));
    } finally {
      setRestoring(null);
    }
  };
  const [checked, setChecked] = useState<Set<number>>(() => topInitialChecked(models));
  // Обновление топа (после очереди) не сбрасывает ни итог, ни ошибки моделей: выбор сверяется с новыми данными.
  const seen = useRef<Set<number>>(new Set(models.flatMap((m) => m.photos.map((p) => p.media_id))));
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setChecked((c) => reconcileChecked(c, seen.current, models));
    seen.current = new Set(models.flatMap((m) => m.photos.map((p) => p.media_id)));
  }, [models]);
  const [drawn, setDrawn] = useState(PAGE);
  const more = useCallback(() => setDrawn((n) => n + PAGE), []);
  const [run, setRun] = useState<{ i: number; of: number } | null>(null);
  const stop = useRef(false);
  useEffect(() => {
    onBusy(run !== null);
  }, [run !== null, onBusy]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(
    () => () => {
      stop.current = true;
      onBusy(false);
    },
    [onBusy],
  );
  const [steps, setSteps] = useState<Record<number, StepResult>>({});
  const [summary, setSummary] = useState<ReturnType<typeof summarize> & { stopped: boolean } | null>(null);

  const total = useMemo(() => models.reduce((n, m) => n + m.photos.length, 0), [models]);
  const nChecked = useMemo(() => models.reduce((n, m) => n + modelChecked(m, checked).length, 0), [models, checked]);
  const toggle = (id: number) => setChecked((c) => setMany(c, [id], !c.has(id)));
  const folderName = (m: CrmTopModel) => {
    const f = m.folders.find((x) => x.id === m.folder_id);
    return f ? `папка «${f.name ?? `Платье #${f.id}`}»` : "новая папка";
  };

  const start = async (mode: "import" | "exclude") => {
    const items = queueBodies(models, checked, mode);
    if (!items.length) return;
    const nIds = items.reduce((n, it) => n + it.body.import_ids.length + it.body.exclude_ids.length, 0);
    if (mode === "exclude" && !window.confirm(`Исключить ${nIds} фото? Больше они не будут предлагаться к импорту.`)) return;
    stop.current = false;
    setSteps({});
    setSummary(null);
    setRun({ i: 0, of: items.length });
    const all: StepResult[] = [];
    const out = await runQueue(items, (it) => runCrmImport(it.model_id, it.body), {
      shouldStop: () => stop.current,
      onStep: (i, res) => {
        all.push({ ...res, model_id: items[i]!.model_id });
        setSteps((s) => {
          const prev = s[items[i]!.model_id];
          return prev && !prev.ok && res.ok ? s : { ...s, [items[i]!.model_id]: res };
        });
        setRun({ i: i + 1, of: items.length });
      },
    });
    setRun(null);
    setSummary({ ...summarize(all), stopped: out.stopped });
    void client.invalidateQueries({ queryKey: topKey(types, limit) });
    void client.invalidateQueries({ queryKey: photoKeys.list });
  };

  const tile = (ph: CrmPhoto) => {
    const on = checked.has(ph.media_id);
    return (
      <button
        key={ph.media_id}
        type="button"
        aria-pressed={on}
        disabled={run !== null}
        onClick={() => toggle(ph.media_id)}
        className={clsx(
          "grid content-start overflow-hidden rounded-[var(--radius-card)] border bg-bg-subtle text-left",
          on ? "border-accent" : "border-border",
        )}
      >
        <div className="relative aspect-[3/4] overflow-hidden bg-bg-elevated">
          <img src={ph.url} loading="lazy" alt="" className="absolute inset-0 h-full w-full object-cover" />
          <span
            className={clsx(
              "absolute left-1.5 top-1.5 grid h-5 w-5 place-items-center rounded border",
              on ? "border-accent bg-accent text-white" : "border-border-strong bg-bg/80",
            )}
          >
            {on ? <Check size={13} /> : null}
          </span>
        </div>
        <div className="px-2 py-1 text-[11.5px] text-fg-muted tnum">
          {GROUP_TITLE[ph.group]} · {ph.score === null ? "нет оценки" : ph.score.toFixed(2)} · {KIND[ph.kind]}
        </div>
        {ph.state === "failed" && ph.error ? <div className="px-2 pb-1 text-[11px] text-warn">{ph.error}</div> : null}
      </button>
    );
  };

  // Порция считается по плиткам всех моделей подряд; счётчики выше — по всему срезу.
  const blocks = drawnBlocks(models, drawn);
  const withNew = models.filter((m) => m.photos.length);
  const step = (m: CrmTopModel) => steps[m.model_id];

  return (
    <div className="grid gap-4">
      <div className="text-[13px] text-fg">
        В топе {num(data.total_models)} моделей, с новыми фото {num(withNew.length)}, новых фото{" "}
        {num(data.new_photos)}
        {data.imported_models > 0 ? (
          <div className="text-[12px] text-fg-muted">
            Модели, из которых уже импортировали фото, в топе не показываются — догрузить их фото можно в карточке
            платья, «Доимпорт из CRM»
          </div>
        ) : null}
      </div>
      {score_error ? (
        <div className="rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1 text-[12px] text-warn">
          Оценки «человек в кадре» недоступны: {score_error}
        </div>
      ) : null}
      {withNew.length === 0 ? <div className="text-[12.5px] text-fg-muted">Новых фото в топе нет</div> : null}

      {blocks.map(({ model: m, photos }) => {
        const ids = m.photos.map((p) => p.media_id);
        const nOn = modelChecked(m, checked).length;
        const s = step(m);
        return (
          <section key={m.model_id} className="grid gap-2" data-annot={`модель ${m.series ?? m.name ?? m.model_id}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-fg">
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  aria-label="вся модель"
                  disabled={run !== null}
                  checked={nOn === ids.length}
                  onChange={(e) => setChecked((c) => setMany(c, ids, e.target.checked))}
                />
                <span className="font-medium">
                  №{m.rank} · {dealsLabel(m)} · {m.series ?? m.name ?? `#${m.model_id}`}
                </span>
              </label>
              <span className="text-fg-muted">{folderName(m)}</span>
              <span className="text-fg-faint tnum">
                отмечено {nOn} из {ids.length}
              </span>
              {s?.ok ? (
                <span className="text-fg-muted tnum">
                  импорт {s.result.imported} · исключено {s.result.excluded} · не скачалось {s.result.failed.length}
                </span>
              ) : null}
              {s && !s.ok ? <span className="text-[12px] text-warn">{s.error}</span> : null}
              <button type="button" className={clsx(btnGhost, "ml-auto")} disabled={run !== null} onClick={() => void exclude(m)}>
                <Ban size={13} /> Не импортировать модель
              </button>
              {exErr[m.model_id] ? <span className="w-full text-[12px] text-warn">{exErr[m.model_id]}</span> : null}
            </div>
            {photos.length ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">{photos.map(tile)}</div>
            ) : null}
          </section>
        );
      })}
      {data.excluded.length ? (
        <details className="text-[12.5px] text-fg-muted">
          <summary className="cursor-pointer">Исключённые модели ({data.excluded.length})</summary>
          <ul className="mt-2 grid gap-1">
            {data.excluded.map((e) => (
              <li key={e.model_id} className="flex items-center gap-3">
                <span className="text-fg">{excludedLabel(e)}</span>
                <span className="text-fg-faint tnum">{e.user_name ? `${e.user_name} · ` : ""}{ruDate(e.date_create)}</span>
                <button type="button" className={btnGhost} disabled={run !== null || restoring !== null} onClick={() => void restore(e.model_id)}>
                  вернуть
                </button>
              </li>
            ))}
          </ul>
          {restoreErr ? <div className="mt-1 text-[12px] text-warn">{restoreErr}</div> : null}
        </details>
      ) : null}
      {drawn < total ? <More key={drawn} onMore={more} text={`показано ${num(Math.min(drawn, total))} из ${num(total)}`} /> : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 grid gap-2 border-t border-border bg-bg px-4 py-3">
        {run ? (
          <div className="flex items-center gap-3 text-[13px] text-fg">
            <span className="tnum">
              модель {Math.min(run.i + 1, run.of)} из {run.of}
            </span>
            <button type="button" className={btnGhost} onClick={() => (stop.current = true)}>
              Остановить
            </button>
            <span className="text-[12px] text-fg-faint">идёт импорт — сначала Остановить</span>
          </div>
        ) : null}
        {summary ? (
          <div className="text-[13px] text-fg">
            {summary.stopped ? "Остановлено. " : ""}
            Импортировано {summary.imported} · исключено {summary.excluded} · не скачалось {summary.failed} · моделей с ошибкой{" "}
            {summary.modelErrors}
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} disabled={run !== null || nChecked === 0} onClick={() => void start("exclude")}>
            Исключить {nChecked}
          </button>
          <button type="button" className={btnPrimary} disabled={run !== null || nChecked === 0} onClick={() => void start("import")}>
            Импортировать {nChecked}
          </button>
        </div>
      </div>
    </div>
  );
}
